import { Card } from '@/components/ui/card';
import { AnalysisIndicator, getStatusColor } from '@/types/analysis';
import { ValueBar } from './ValueBar';
import { 
  TrendingDown, 
  TrendingUp, 
  CheckCircle2, 
  AlertTriangle,
  Stethoscope,
  Lightbulb
} from 'lucide-react';

interface IndicatorCardProps {
  indicator: AnalysisIndicator;
  index: number;
}

export function IndicatorCard({ indicator, index }: IndicatorCardProps) {
  const colors = getStatusColor(indicator.status);
  
  const StatusIcon = indicator.status === 'normal' 
    ? CheckCircle2
    : indicator.status === 'low' || indicator.status === 'critical_low'
    ? TrendingDown
    : TrendingUp;

  const statusLabel = indicator.status === 'normal' 
    ? 'В норме'
    : indicator.status === 'low'
    ? 'Понижен'
    : indicator.status === 'high'
    ? 'Повышен'
    : indicator.status === 'critical_low'
    ? 'Критически низкий'
    : 'Критически высокий';

  return (
    <Card 
      className={`p-4 border-2 ${colors.border} ${colors.bg} animate-fade-in`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Header */}
      <div className="mb-3">
        {/* Status badge on top */}
        <div className="flex justify-end mb-2">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 border ${colors.border}`}>
            <StatusIcon className={`w-4 h-4 lg:w-5 lg:h-5 ${colors.text}`} />
            <span className={`text-xs sm:text-sm font-medium ${colors.text} whitespace-nowrap`}>
              {statusLabel}
            </span>
          </div>
        </div>

        {/* Full-width title */}
        <h4 className="font-bold text-foreground text-base sm:text-lg lg:text-xl leading-snug break-words hyphens-auto">
          {indicator.name}
        </h4>

        {/* Value */}
        <div className="flex items-baseline gap-2 mt-2 flex-wrap">
          <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
            {indicator.value}
          </span>
          <span className="text-sm sm:text-base text-muted-foreground">{indicator.unit}</span>
        </div>
      </div>

      {/* Value Bar */}
      <ValueBar indicator={indicator} />

      {/* Explanation */}
      <div className="mt-4 p-3 sm:p-4 bg-white/60 rounded-lg border border-border/30">
        <div className="flex items-start gap-2">
          <Lightbulb className="w-5 h-5 lg:w-6 lg:h-6 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-base sm:text-lg font-semibold text-foreground mb-1">Что это значит:</p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              {indicator.explanation}
            </p>
          </div>
        </div>
      </div>

      {/* Recommendation */}
      <div className="mt-3 p-3 sm:p-4 bg-white/60 rounded-lg border border-border/30">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 lg:w-6 lg:h-6 text-warning mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-base sm:text-lg font-semibold text-foreground mb-1">Рекомендация:</p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              {indicator.recommendation}
            </p>
          </div>
        </div>
      </div>

      {/* Specialist */}
      {indicator.specialist && (
        <div className="mt-3 flex items-center gap-2 text-sm sm:text-base flex-wrap">
          <Stethoscope className="w-4 h-4 lg:w-5 lg:h-5 text-primary" />
          <span className="text-muted-foreground">К кому обратиться:</span>
          <span className="font-medium text-primary">{indicator.specialist}</span>
        </div>
      )}
    </Card>
  );
}
